import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { FileSpreadsheet, Loader2 } from 'lucide-react'
import saveAs from 'file-saver'
import Cookies from 'js-cookie'
import FilePreviewModal from '@/components/file-preview/FilePreviewModal'
import type { PreviewFile } from '@/components/file-preview/types'
import { API_URL } from '../../../utils'

interface ExportEvaluationXLSXProps {
  tender: { id: string; name: string }
  round?: number
}

const ExportEvaluationXLSX: React.FC<ExportEvaluationXLSXProps> = ({ tender, round }) => {
  const { t } = useTranslation()
  const [generating, setGenerating] = useState(false)
  const [preview, setPreview] = useState<{ blob: Blob; url: string } | null>(null)

  const filename = `${tender.name} - Evaluation.xlsx`

  const handleExport = async () => {
    setGenerating(true)
    try {
      const params = round != null ? `?round=${round}` : ''
      const token = Cookies.get('token')
      const response = await fetch(`${API_URL}/download/evaluation/${tender.id}${params}`, {
        headers: token ? { Authorization: token } : undefined,
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }))
        throw new Error(err.error || 'Export failed')
      }

      const buffer = await response.arrayBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      setPreview({ blob, url })
    } catch (err) {
      console.error('Export evaluation XLSX failed:', err)
    } finally {
      setGenerating(false)
    }
  }

  const handlePreviewClose = useCallback(() => {
    if (preview) {
      URL.revokeObjectURL(preview.url)
    }
    setPreview(null)
  }, [preview])

  const handleDownload = useCallback(() => {
    if (preview) {
      saveAs(preview.blob, filename)
    }
  }, [preview, filename])

  const previewFiles: PreviewFile[] = preview
    ? [{ url: preview.url, title: filename, type: 'excel' }]
    : []

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={generating}
      >
        {generating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileSpreadsheet className="h-4 w-4" />
        )}
        {t('Export_evaluation_xlsx', 'Export Evaluation')}
      </Button>

      <FilePreviewModal
        isOpen={!!preview}
        onClose={handlePreviewClose}
        files={previewFiles}
        currentIndex={0}
        onDownload={handleDownload}
      />
    </>
  )
}

export default ExportEvaluationXLSX
